// Sekwencja części 2 hero 47-A „GENERATOR FANOWSKICH EDITÓW” (paczka H47, MAPA §2, część 2): gra RAZ.
//
// Warstwa ruchu (`.mp-hg-war`: łączniki na 1440, iskry słupek fali → komórka siatki, lecące edity komórka →
// konto) zależy od układu, więc jest mierzona z DOM raz po montażu, po `document.fonts.ready` i przy
// zmianie rozmiaru (port `lacznikiGen` z `47a-swiat.js`), a jej elementy dostają położenie przez styl.
// Pomiar i uzbrojenie stoją w `useLayoutEffect`: łączniki mają położenie, zanim cokolwiek się namaluje.
//
// Stan wyjściowy sekwencji (animacje zatrzymane w t = 0) dostają WYŁĄCZNIE elementy, których przy montażu nie
// widać (całe poza oknem): element widoczny przy montażu stoi w spoczynku i nie mignie (0 przejść
// „widoczne → ukryte”). Sekwencja rusza, gdy 30 % kroków wejdzie w widok, i stoi, gdy cała sekcja zjedzie z okna;
// po jej końcu (+ 200 ms), gdy któraś komórka wideo przecina okno, `onVideos` każe siatce wstawić trzy małe wideo
// (do tej chwili nie ma ani jednego <video>, więc nic się nie pobiera); potem KAŻDE wideo gra tylko wtedy, gdy
// jego komórka przecina okno. Przy `prefers-reduced-motion` nic nie gra, komórki stoją na plakacie, a warstwa ma
// tylko położenie (łączniki 1440).

import { useLayoutEffect } from 'react'
import type { RefObject } from 'react'
import { plan2 } from '../lib/landingParabola'
import type { WarstwaGen } from '../lib/landingParabola'
import { canAnimate, odpal } from './useHeroMotion'

const r1 = (v: number) => Math.round(v * 10) / 10
/** Najwyżej tyle wideo gra naraz (MAPA §5). */
export const MAX_VIDEOS = 3

type Box = { x: number; y: number; w: number; h: number }
function wzgl(el: Element, ref: Element): Box {
  const a = el.getBoundingClientRect(), b = ref.getBoundingClientRect()
  return { x: a.left - b.left, y: a.top - b.top, w: a.width, h: a.height }
}

/** Pomiar warstwy z układu w spoczynku (port `lacznikiGen`): łączniki tylko na 1440, iskry, lecące edity. */
export function zmierz(sec: Element, wide: boolean): WarstwaGen {
  const ut = sec.querySelector('.mp-hg-ut'), siat = sec.querySelector('.mp-hg-siat')
  const kon = [...sec.querySelectorAll('.mp-hg-kn .mp-hp-ak-in')]
  if (!ut || !siat) return { lacz: [], iskry: [], loty: [] }
  const U = wzgl(ut, sec), S = wzgl(siat, sec), ka = [...siat.querySelectorAll<HTMLElement>('.mp-hg-ka')], kol = wide ? 8 : 6
  const lacz: WarstwaGen['lacz'] = []
  const ln = (a: [number, number], b: [number, number], g: 'a' | 'b') => {
    const dx = b[0] - a[0], dy = b[1] - a[1]
    lacz.push({ g, x: r1(a[0]), y: r1(a[1]), w: r1(Math.sqrt(dx * dx + dy * dy)), a: r1(Math.atan2(dy, dx) * 180 / Math.PI) })
  }
  if (wide && ka.length) {
    const z: [number, number] = [U.x + U.w + 6, U.y + U.h / 2]
    for (let i = 0; i < 3; i++) { const c0 = wzgl(ka[i * kol], sec); ln(z, [S.x - 6, c0.y + c0.h / 2], 'a') }
    for (const k of kon) { const K = wzgl(k, sec); ln([S.x + S.w + 6, K.y + K.h / 2], [K.x - 8, K.y + K.h / 2], 'b') }
  }
  const fal = [...ut.querySelectorAll('.mp-hp-fala > i')]
  const iskry = fal.length ? ka.map((k, j) => {
    const F = wzgl(fal[j % fal.length], sec), C = wzgl(k, sec)
    return { x: r1(F.x + F.w / 2), y: r1(F.y + F.h / 2), dx: r1(C.x + C.w / 2 - F.x - F.w / 2), dy: r1(C.y + C.h / 2 - F.y - F.h / 2) }
  }) : []
  const loty = ka.length ? kon.map((k, j) => {
    const src = wide ? ka[Math.min(ka.length - 1, (j >> 1) * kol + kol - 1)] : ka[ka.length - kol + j]
    const C = wzgl(src, sec), K = wzgl(k, sec), w = C.w * 0.62, h = C.h * 0.62
    return { x: r1(C.x + C.w / 2 - w / 2), y: r1(C.y + C.h / 2 - h / 2), w: r1(w), h: r1(h),
      dx: r1(K.x + K.w / 2 - C.x - C.w / 2), dy: r1(K.y + K.h / 2 - C.y - C.h / 2), bg: src.style.backgroundImage }
  }) : []
  return { lacz, iskry, loty }
}

/** Zmierzona warstwa → style jej elementów (łącznik: położenie, długość, obrót w spoczynku). */
function zapisz(sec: Element, war: WarstwaGen): void {
  const lines = { a: 0, b: 0 }
  for (const l of war.lacz) {
    const el = sec.querySelector<HTMLElement>(`.mp-hg-ln[data-g="${l.g}"][data-i="${lines[l.g]++}"]`)
    if (el) Object.assign(el.style, { left: `${l.x}px`, top: `${l.y}px`, width: `${l.w}px`, transform: `rotate(${l.a}deg)` })
  }
  war.iskry.forEach((s, j) => {
    const el = sec.querySelector<HTMLElement>(`.mp-hg-isk[data-j="${j}"]`)
    if (el) Object.assign(el.style, { left: `${s.x}px`, top: `${s.y}px` })
  })
  war.loty.forEach((f, j) => {
    const el = sec.querySelector<HTMLElement>(`.mp-hg-lot[data-j="${j}"]`)
    if (el) Object.assign(el.style, { left: `${f.x}px`, top: `${f.y}px`, width: `${f.w}px`, height: `${f.h}px`, backgroundImage: f.bg })
  })
}

/** Czy element przecina okno (wtedy przy montażu nie wolno go ukryć). */
function onScreen(el: Element): boolean {
  const r = el.getBoundingClientRect()
  return r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth
}

/**
 * Pomiar warstwy, uzbrojenie sekwencji, wyzwalacz 30 % kroków i wideo po sekwencji dla sekcji części 2.
 * `wide` = siatka 8 × 3 z łącznikami (od 1336 px), `enabled` = ruch dozwolony.
 */
export function useGeneratorSequence(sec: RefObject<HTMLElement | null>, wide: boolean, enabled: boolean, onVideos: () => void): void {
  useLayoutEffect(() => {
    const s = sec.current
    if (!s || typeof window === 'undefined') return
    const motion = enabled && canAnimate(s)
    const cache = new Map<string, Element | null>()
    const kom = s.querySelectorAll('.mp-hg-ka').length
    let anims: Animation[] = []
    let played = !motion
    let armed = false
    let alive = true
    let raf = 0
    let timer = 0
    /** Sekwencja dobiegła końca (albo jej nie ma): od tej chwili wideo może grać. */
    let done = !motion
    let videosOn = false
    /** Zmiana rozmiaru w trakcie sekwencji: pomiar dopiero po jej końcu (komórki i konta są wtedy w skali < 1). */
    let resizePending = false
    /** Selektory zdarzeń, których elementy były widoczne przy pierwszym uzbrojeniu: te nigdy nie znikają. */
    let keep: Set<string> | null = null
    // Trzy komórki wideo i ich WŁASNA widoczność (przegląd Codex H47-CODEX-03: wcześniej wideo słuchało listy
    // kroków, więc na telefonie grało pod krawędzią okna, gdy w oknie był jeszcze krok 01).
    const cells = [...s.querySelectorAll<HTMLElement>('.mp-hg-ka[data-video]')].slice(0, MAX_VIDEOS)
    const cellIn = new Map<Element, boolean>()

    /** Wideo gra wtedy i tylko wtedy, gdy sekwencja się skończyła, a jego komórka przecina okno. */
    const wideo = () => {
      if (!done || !alive) return
      if (!videosOn) {
        // pierwsze wideo dopiero przy widocznej komórce: do tej chwili nie ma <video>, więc nic się nie pobiera
        if (!cells.some((c) => cellIn.get(c))) return
        videosOn = true
        onVideos()
        return
      }
      for (const c of cells) {
        const v = c.querySelector('video')
        if (!v) continue
        if (cellIn.get(c)) { const pr = v.play(); if (pr && typeof pr.catch === 'function') pr.catch(() => {}) } else v.pause()
      }
    }
    /** Koniec sekwencji = koniec wszystkich jej animacji (wstrzymanie poza ekranem przesuwa go razem z nimi). */
    const whenDone = () => {
      Promise.all(anims.map((a) => a.finished)).then(() => {
        if (!alive) return
        timer = window.setTimeout(() => {
          timer = 0
          done = true
          if (resizePending) { resizePending = false; sync() }
          wideo()
        }, 200)
      }).catch(() => { /* anulowane przy odmontowaniu */ })
    }
    const arm = (war: WarstwaGen) => {
      const ev = plan2(wide ? 'h' : 'v', war, kom).ev
      if (!keep) {
        keep = new Set()
        for (const e of ev) {
          const el = s.querySelector(e.sel)
          // iskry i lecące edity są ukryte w spoczynku, więc ich ruch niczego nie ukrywa
          if (el && !el.closest('.mp-hg-war .mp-hg-isk, .mp-hg-war .mp-hg-lot') && onScreen(el)) keep.add(e.sel)
        }
      }
      const kept = keep
      anims = odpal(s, ev.filter((e) => !kept.has(e.sel)), true, cache)
      armed = true
    }
    const sync = () => {
      raf = 0
      if (!alive) return
      // W trakcie sekwencji warstwa zostaje z pomiaru sprzed startu (przegląd Claude H47-CLAUDE-03: pomiar przez
      // getBoundingClientRect widział komórki w skali 0,2 i lecące edity dostawały 5 x mniejszy rozmiar).
      if (played && !done) { resizePending = true; return }
      if (armed) { for (const a of anims) a.cancel(); anims = []; armed = false }
      const war = zmierz(s, wide)
      zapisz(s, war)
      if (!played) arm(war)
    }
    sync()

    const onResize = () => { if (!raf) raf = requestAnimationFrame(sync) }
    window.addEventListener('resize', onResize)
    // Pierwszy pomiar może wypaść przed czcionkami (inna wysokość tytułów kroków): drugi po ich wczytaniu.
    document.fonts?.ready?.then(() => { if (alive) onResize() }).catch(() => {})

    // Sekwencja: start po wejściu 30 % kroków; gdy CAŁA sekcja zjedzie z okna w trakcie sekwencji, animacje stoją
    // (przegląd Codex H47-CODEX-02: wcześniej grały do końca poza ekranem), a po powrocie jadą dalej od tej samej
    // chwili — sekwencja nadal gra raz.
    const kroki = s.querySelector('.mp-hg-kroki')
    const io = motion && kroki ? new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.target === s) {
          if (!played || done) continue
          for (const a of anims) {
            if (!e.isIntersecting && a.playState === 'running') a.pause()
            else if (e.isIntersecting && a.playState === 'paused') a.play()
          }
        } else if (e.isIntersecting && e.intersectionRatio >= 0.3 && !played) {
          played = true
          armed = false
          for (const a of anims) a.play()
          whenDone()
        }
      }
    }, { threshold: [0, 0.3] }) : null
    if (io && kroki) { io.observe(kroki); io.observe(s) }
    const vio = motion && cells.length ? new IntersectionObserver((entries) => {
      for (const e of entries) cellIn.set(e.target, e.isIntersecting)
      wideo()
    }, { threshold: 0 }) : null
    for (const c of cells) vio?.observe(c)
    // <video> wstawia React po `onVideos`: nowy element od razu dostaje stan widoczności swojej komórki.
    const grid = s.querySelector('.mp-hg-siat')
    const mo = vio && grid && typeof MutationObserver === 'function' ? new MutationObserver(() => { if (videosOn) wideo() }) : null
    if (mo && grid) mo.observe(grid, { childList: true, subtree: true })

    return () => {
      alive = false
      io?.disconnect()
      vio?.disconnect()
      mo?.disconnect()
      window.removeEventListener('resize', onResize)
      if (raf) cancelAnimationFrame(raf)
      if (timer) window.clearTimeout(timer)
      for (const a of anims) a.cancel()
      for (const c of cells) c.querySelector('video')?.pause()
    }
  }, [sec, wide, enabled, onVideos])
}
